import {React, useState, useEffect} from 'react';
import getBackend from 'backend/backend';
import { Paths } from 'paths';
import { useTranslation } from "react-i18next";
import { useParams } from 'react-router-dom';
import { Alert, Container, Table, Card, Row, Col, Badge } from 'react-bootstrap';
import SidebarPage from 'components/sidebar_page';
import { timestampToStringWithMillis } from 'components/misc';

const WorkerLogs = (props) => {
    const [t] = useTranslation('common');
    const [logs, setLogs] = useState([]);
    const [error, setError] = useState(null);
    const [levelFilter, setLevelFilter] = useState('');
    const [componentFilter, setComponentFilter] = useState('');
    const [timeRange, setTimeRange] = useState({ from: Date.now() - 1 * 60 * 60 * 1000, to: Date.now() });

    useEffect(() => {
        let canceled = false;
        const fetchLogs = async () => {
            const result = await getBackend().getWorkerLogs(props.workerId, timeRange.from, timeRange.to);
            if (!canceled && result.error == null) {
                var logs = [];

                for (const log of result.response.logs) {
                    if (levelFilter !== '' && log.level !== levelFilter)
                        continue;

                    if (componentFilter !== '' && log.component !== componentFilter)
                        continue;

                    logs.push({
                        id: log.id,
                        timestamp: log.timestamp,
                        level: log.level,
                        component: log.component,
                        message: log.message,
                        error: log.error,
                    });
                }

                setLogs(logs);
            } else if (!canceled) {
                setError(result.error);
            }
        }
        fetchLogs();

        return () => {
            canceled = true;
        };
    }, [props.workerId, props.updateSeqNo, levelFilter, componentFilter, timeRange]);

    return (
        <>
            <h3 className="mb-4">{t('Logs')}</h3>
            {error && <Alert variant="danger">{error}</Alert>}
            <div className="mb-3 d-flex gap-3">
                <div>
                    <label htmlFor="durationFilter" className="form-label">{t('Filter by Duration')}</label>
                    <select 
                        id="durationFilter" 
                        className="form-select" 
                        onChange={(e) => {
                            const hours = parseInt(e.target.value);
                            const to = Date.now();
                            const from = to - hours * 60 * 60 * 1000;
                            setTimeRange({ from: from, to: to });
                        }}
                    >
                        <option value="1">{t('Last 1h')}</option>
                        <option value="3">{t('Last 3h')}</option>
                        <option value="12">{t('Last 12h')}</option>
                        <option value="24">{t('Last 24h')}</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="levelFilter" className="form-label">{t('Filter by Level')}</label>
                    <select 
                        id="levelFilter" 
                        className="form-select" 
                        onChange={(e) => setLevelFilter(e.target.value)}
                    >
                        <option value="">{t('All')}</option>
                        <option value="info">{t('Info')}</option>
                        <option value="error">{t('Error')}</option>
                        <option value="warning">{t('Warning')}</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="componentFilter" className="form-label">{t('Filter by Component')}</label>
                    <input 
                        type="text" 
                        id="componentFilter" 
                        className="form-control" 
                        placeholder={t('Enter component name')} 
                        onChange={(e) => setComponentFilter(e.target.value)}
                    />
                </div>
            </div>
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>{t('Timestamp')}</th>
                        <th>{t('Level')}</th>
                        <th>{t('Component')}</th>
                        <th>{t('Message')}</th>
                        <th>{t('Error')}</th>
                    </tr>
                </thead>
                <tbody>
                    {logs.map((log) => (
                            <tr key={log.id}>
                                <td>{timestampToStringWithMillis(log.timestamp/1000)}</td>
                                <td>{log.level}</td>
                                <td>{log.component}</td>
                                <td>{log.message}</td>
                                <td>{log.error}</td>
                            </tr>
                        ))
                    }
                </tbody>
            </Table>
        </>
    );
}

const HostInfo = (props) => {
    const [t] = useTranslation('common');
    const hostInfo = props.hostInfo;

    return (
        <>
            <h3 className="mb-4">{t('GPUs')}</h3>
            {hostInfo && hostInfo.gpus && hostInfo.gpus.length > 0 ? (
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>{t('Index')}</th>
                            <th>{t('Model')}</th>
                            <th>{t('Used Memory')} (MiB)</th>
                            <th>{t('Total Memory')} (MiB)</th>
                            <th>{t('Utilization GPU')} (%)</th>
                            <th>{t('Utilization Memory')} (%)</th>
                            <th>{t('Temperature')} (°C)</th>
                            <th>{t('Power')} (W)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {hostInfo.gpus.map((gpu, index) => (
                            <tr key={index}>
                                <td>{gpu.index}</td>
                                <td>{gpu.name}</td>
                                <td>{gpu.used_memory}</td>
                                <td>{gpu.total_memory}</td>
                                <td>{gpu.utilization_gpu}</td>
                                <td>{gpu.utilization_memory}</td>
                                <td>{gpu.temperature}</td>
                                <td>{gpu.power_draw}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ) : (
                <p>{t('No GPU information available')}</p>
            )}
            {hostInfo && hostInfo.gpus_error && (
                <Alert variant="danger">GPUs info error: {hostInfo.gpus_error}</Alert>
            )}
        </>
    )
}

const Worker = () => {
    const workerId = useParams().workerId;
    const [worker, setWorker] = useState(null);
    const [t] = useTranslation('common');
    const [error, setError] = useState(null);
    const [updateSeqNo, setUpdateSeqNo] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setUpdateSeqNo(prev => prev + 1);
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        let canceled = false;

        const getWorker = async () => {
            const result = await getBackend().getWorker(workerId);
            if (canceled)
                return;

            if (result.error == null) {
                setWorker(result.response.worker);
            } else {
                setError(result.error);
                window.location.href = Paths.NotFound.path;
            }
        };

        getWorker();

        return async () => {
            canceled = true;
        }
    }, [workerId, updateSeqNo]);

    return (
        <SidebarPage title={t('Worker')} description={t('Worker')}>
            {error && <Alert variant="danger">{error}</Alert>}
            {worker &&
            <Container className="my-5">
                <h2 className="mb-4">{t('Worker')}</h2>
                <Card className="mb-4 shadow-sm">
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <h5 className="text-muted">ID</h5>
                                    <div className="font-monospace">{worker.id}</div>
                                </div>
                                <div className="mb-3">
                                    <h5 className="text-muted">Hostname</h5>
                                    <div>{worker.hostname}</div>
                                </div>
                                <div className="mb-3">
                                    <h5 className="text-muted">Model</h5>
                                    <div>{worker.model}</div>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <h5 className="text-muted">State</h5>
                                    <Badge bg={worker.state === "running" ? "success" : "warning"} className="px-3 py-2">
                                        {worker.state}
                                    </Badge>
                                </div>
                                <div className="mb-3">
                                    <h5 className="text-muted">Last Seen</h5>
                                    <div>{new Date(worker.last_seen).toLocaleString()}</div>
                                </div>
                                <div className="mb-3">
                                    <h5 className="text-muted">Last Error</h5>
                                    {worker.last_error ? (
                                        <div className="text-danger">{worker.last_error}</div>
                                    ) : (
                                        <div className="text-muted">None</div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
                <HostInfo hostInfo={worker.host_info} />
                <WorkerLogs workerId={workerId} updateSeqNo={updateSeqNo} />
            </Container>
            }
        </SidebarPage>
    );
};

export default Worker;
